'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const Header: React.FC = () => {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/homepage', label: 'Work' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled
          ? 'rgba(15, 15, 15, 0.85)'
          : 'rgba(15, 15, 15, 0.4)',
        backdropFilter: scrolled ? 'blur(24px)' : 'blur(12px)',
        WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'blur(12px)',
        borderBottom: scrolled
          ? '1px solid rgba(255,255,255,0.07)'
          : '1px solid transparent',
      }}
    >
      <div
        className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between"
        style={{ height: '68px' }}
      >
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2 group">
          <AppLogo size={36} />
          <span
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontWeight: 700,
              fontSize: '18px',
              letterSpacing: '-0.03em',
              color: '#f5f5f5',
            }}
          >
            VideoPortfolio
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-2">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: isActive ? '#fca5a5' : '#6b7280',
                  textDecoration: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  transition: 'color 0.2s ease, background 0.2s ease, transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease',
                  background: isActive
                    ? 'rgba(220, 38, 38, 0.12)'
                    : 'transparent',
                  border: isActive
                    ? '1px solid rgba(220, 38, 38, 0.2)'
                    : '1px solid transparent',
                  display: 'inline-block',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.color = '#f5f5f5';
                    (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 0 16px rgba(220, 38, 38, 0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.color = '#6b7280';
                    (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }
                }}
              >
                {link.label}
              </Link>
            );
          })}

          <Link
            href="/contact"
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              color: '#f5f5f5',
              textDecoration: 'none',
              padding: '9px 20px',
              borderRadius: '10px',
              background: 'rgba(220, 38, 38, 0.9)',
              border: '1px solid rgba(220, 38, 38, 0.5)',
              marginLeft: '8px',
              transition: 'background 0.25s ease, box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
              display: 'inline-block',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                'rgba(220, 38, 38, 1)';
              (e.currentTarget as HTMLElement).style.boxShadow =
                '0 0 24px rgba(220, 38, 38, 0.5), 0 4px 16px rgba(220, 38, 38, 0.3)';
              (e.currentTarget as HTMLElement).style.transform = 'scale(1.06)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                'rgba(220, 38, 38, 0.9)';
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
            }}
          >
            Hire Me
          </Link>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          style={{ cursor: 'pointer' }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                display: 'block',
                width: '22px',
                height: '2px',
                background: '#f5f5f5',
                borderRadius: '2px',
                transition: 'transform 0.3s ease, opacity 0.3s ease',
                transform:
                  menuOpen && i === 0
                    ? 'translateY(6px) rotate(45deg)'
                    : menuOpen && i === 2
                    ? 'translateY(-6px) rotate(-45deg)'
                    : menuOpen && i === 1
                    ? 'scaleX(0)'
                    : 'none',
                opacity: menuOpen && i === 1 ? 0 : 1,
              }}
            />
          ))}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        style={{
          overflow: 'hidden',
          maxHeight: menuOpen ? '300px' : '0',
          transition: 'max-height 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          background: 'rgba(15, 15, 15, 0.95)',
          backdropFilter: 'blur(24px)',
          borderBottom: menuOpen ? '1px solid rgba(255,255,255,0.07)' : 'none',
        }}
      >
        <nav className="flex flex-col px-6 py-4 gap-1">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: isActive ? '#fca5a5' : '#9ca3af',
                  textDecoration: 'none',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  background: isActive
                    ? 'rgba(220, 38, 38, 0.1)'
                    : 'transparent',
                }}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            href="/contact"
            onClick={() => setMenuOpen(false)}
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              color: '#f5f5f5',
              textDecoration: 'none',
              padding: '12px 16px',
              borderRadius: '10px',
              background: 'rgba(220, 38, 38, 0.8)',
              marginTop: '8px',
              textAlign: 'center',
            }}
          >
            Hire Me
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;